<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
import { onClickOutside, useFocus } from '@vueuse/core'
import { onMounted, reactive, ref, watchEffect } from 'vue'
import { Button } from '@/foundation/button'
import { Input } from '@/foundation/input'
import { Label } from '@/foundation/label'
import { Switch } from '@/foundation/switch'
import { Icon } from '@/notebook/components/icons'
import { useLocale } from '@/notebook/locales'

type Props = {
  editor: Editor
}
const props = withDefaults(defineProps<Props>(), {})
const emits = defineEmits(['onSetLink', 'onClickOutside'])

const { t } = useLocale()

let form = reactive({
  text: '',
  link: '',
})
const inputRef = ref<HTMLInputElement | null>(null)
const { focused } = useFocus(inputRef)
const openInNewTab = ref<boolean>(false)
const target = ref(null)

onClickOutside(target, (event) => emits('onClickOutside', event))

watchEffect(() => {
  const { href: link, target } = props.editor.getAttributes('link')
  const { from, to } = props.editor.state.selection
  const text = props.editor.state.doc.textBetween(from, to, ' ')

  form = {
    link,
    text,
  }
  openInNewTab.value = target === '_blank'
})
function handleSubmit() {
  emits('onSetLink', form.link, form.text || form.link, openInNewTab.value)
}
onMounted(() => {
  focused.value = true
})
</script>

<template>
  <div ref="target" class="p-2 bg-white rounded-lg dark:bg-black shadow-sm border border-neutral-200 dark:border-neutral-800">
    <form class="flex flex-col gap-2" @submit.prevent="handleSubmit">
      <Label> {{ t('editor.link.dialog.text') }} </Label>
      <div class="flex w-full max-w-sm items-center gap-1.5">
        <div class="relative w-full max-w-sm items-center">
          <Input v-model="form.text" type="text" class="w-80" />
        </div>
      </div>
      <Label>{{ t('editor.link.dialog.link') }}</Label>
      <div class="flex w-full max-w-sm items-center gap-1.5">
        <div class="relative w-full max-w-sm items-center">
          <Input ref="inputRef" v-model="form.link" type="url" required class="pl-10" />
          <span class="absolute start-0 inset-y-0 flex items-center justify-center px-2">
            <Icon class="size-5 text-muted-foreground" name="Link" />
          </span>
        </div>
      </div>
      <div class="flex items-center space-x-2 mt-1">
        <Checkbox id="openInNewTab" v-model:checked="openInNewTab" />
        <Label for="openInNewTab">{{ t('editor.link.dialog.openInNewTab') }}</Label>
      </div>
      <Button type="submit" class="mt-2 self-end">{{ t('editor.link.dialog.button.apply') }} </Button>
    </form>
  </div>
</template>
